/**
 * A mixed SERP is only a decision when your page's own type is not in the mix.
 *
 * "Mixed" means Google is ranking several shapes for one query — a definition,
 * a how-to and a template all on page one. The card treated that as a choice to
 * make, and told the operator to "pick one page type to align with" even when
 * their page was ALREADY one of the shapes ranking. On
 * `/templates/project-scope-statement` the SERP mix is definition / how-to /
 * template, the page is a template, and smartsheet.com ranks #5 with the same
 * type. There is nothing to pick: the question the card asks was answered
 * before it was asked.
 *
 * The mix still matters when your type is absent — then you are the odd shape
 * out and choosing which winner to look like is real work. So the check is
 * membership, not consensus.
 */

export interface PageTypeRow {
  position: number;
  family: string;
  domain?: string;
}

export interface MixedSerpAlignment {
  /** Your page's type, as it ranks somewhere on this SERP. */
  family: string;
  /** The best-placed rival that shares it, when we know who. */
  position: number | null;
  domain: string | null;
}

const UNKNOWN = new Set(['', 'unknown', 'unclear', 'mixed']);

/**
 * Null unless the SERP is mixed AND your type is one of the shapes on it.
 *
 * `rows` are the SERP's own results; `samples` carry the domain names where the
 * diagnosis captured them, so the answer can name who rather than just assert
 * it. Rows without a usable family are ignored rather than treated as a match —
 * "unknown" is the absence of a classification, not a type your page shares.
 */
export function mixedSerpAlignment(
  serpFamily: string | null | undefined,
  yourFamily: string | null | undefined,
  rows: readonly PageTypeRow[] | null | undefined,
  samples?: readonly PageTypeRow[] | null
): MixedSerpAlignment | null {
  if ((serpFamily || '').trim() !== 'mixed') return null;

  const yours = (yourFamily || '').trim().toLowerCase();
  if (!yours || UNKNOWN.has(yours)) return null;

  const matches = [...(rows || []), ...(samples || [])]
    .filter((row) => (row?.family || '').trim().toLowerCase() === yours)
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  if (!matches.length) return null;

  // The best-placed match names it, and a sample is preferred at equal position
  // because only samples carry a domain.
  const named = matches.find((row) => (row.domain || '').trim());
  const best = named && named.position === matches[0].position ? named : matches[0];

  return {
    family: yours,
    position: best.position || null,
    domain: (best.domain || '').trim() || null
  };
}

/** One SERP result offered as an example of the shape Google is rewarding. */
export interface PageTypeExample {
  position: number;
  domain: string;
  title: string;
}

export interface PageTypeExampleRow extends PageTypeRow {
  title?: string;
}

const MAX_EXAMPLES = 2;

/**
 * The best-placed results that ARE the type the SERP expects.
 *
 * The card asserts "SERP expects definition" and, until this, gave nothing to
 * check it against: the operator either trusted the verdict or opened Google
 * themselves, which is the clicking-around the review belt exists to remove.
 * Two titles make the claim falsifiable at a glance, and they double as the
 * brief — this is the shape to write.
 *
 * Two, not five. The card is one of eight on a belt page and the third example
 * adds confidence to nobody who was not already convinced by the first.
 *
 * Deliberately silent when:
 * - the SERP is `mixed` — that case has its own table, which shows the spread
 *   rather than a consensus, and two "examples" of a consensus that does not
 *   exist would be a lie;
 * - the family is unknown — there is no claim to support;
 * - a result is your own page — telling someone to look at the page they are
 *   deciding about is noise, whatever its type.
 */
export function serpPageTypeExamples(
  serpFamily: string | null | undefined,
  rows: readonly PageTypeExampleRow[] | null | undefined,
  yourUrlOrDomain?: string | null
): PageTypeExample[] {
  const family = (serpFamily || '').trim().toLowerCase();
  if (!family || UNKNOWN.has(family)) return [];

  const yours = (yourUrlOrDomain || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0];

  return [...(rows || [])]
    .filter((row) => (row?.family || '').trim().toLowerCase() === family)
    .filter((row) => {
      const domain = (row.domain || '').trim().toLowerCase().replace(/^www\./, '');
      const title = (row.title || '').trim();
      if (!domain || !title) return false;
      return !yours || domain !== yours;
    })
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .slice(0, MAX_EXAMPLES)
    .map((row) => ({
      position: row.position || 0,
      domain: (row.domain || '').trim().replace(/^www\./, ''),
      title: (row.title || '').trim()
    }));
}
